import React, { useState } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import api from "../api";
import type { ProjectSummary } from "../api";
import { useApi } from "../useApi";
import { eC } from "../lib/helpers";

export function CostsPage({ projects }: { projects: ProjectSummary[] }) {
  const [selectedProject, setSelectedProject] = useState(projects[0]?.project_id || "");
  const [days, setDays] = useState(30);
  const { data: costs, loading, error, refetch } = useApi(
    () => selectedProject ? api.getCosts(selectedProject, days).catch(() => null) : Promise.resolve(null),
    [selectedProject, days]
  );
  const { data: usage } = useApi(() => api.getUsage().catch(() => null), []);

  return (
    <div>
      <div className="page-title-row">
        <h2 className="page-title">Costs</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select className="input input-mono" style={{ width: "auto", fontSize: "var(--text-sm)" }} value={selectedProject} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedProject(e.target.value)}>
            {projects.filter((p: ProjectSummary) => p.project_id !== "example-app").map((p: ProjectSummary) => (
              <option key={p.project_id} value={p.project_id}>{p.display_name}</option>
            ))}
          </select>
          <select className="input" style={{ width: "auto", fontSize: "var(--text-sm)" }} value={days} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setDays(Number(e.target.value))}>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
          <button className="btn btn-sm" onClick={refetch}><RefreshCw size={11} /></button>
        </div>
      </div>

      {error && (
        <div className="card" style={{ background: "var(--red-dim)", borderColor: "var(--red)", marginBottom: 12, padding: "8px 12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AlertTriangle size={14} style={{ color: "var(--red)" }} />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--red)", flex: 1 }}>{error}</span>
            <button className="btn btn-sm" onClick={refetch}><RefreshCw size={11} /> Retry</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="empty-state"><div className="spinner" /><span className="muted">Loading costs...</span></div>
      ) : costs ? (
        <div>
          <div className="mono" style={{ fontSize: "var(--text-xl)", fontWeight: "var(--weight-semibold)", color: "var(--text)", marginBottom: 16 }}>${costs.total?.toFixed(2) || "0.00"}</div>
          {costs.by_engine && Object.keys(costs.by_engine).length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
              {Object.entries(costs.by_engine).map(([engine, cost]: [string, unknown]) => (
                <div key={engine} className="card">
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                    <span className="project-dot" style={{ background: eC(engine) }} />
                    <span style={{ fontSize: "var(--text-sm)", fontWeight: 500, color: "var(--text)" }}>{engine}</span>
                  </div>
                  <div className="stat-value">${(cost as number).toFixed(2)}</div>
                </div>
              ))}
            </div>
          )}
          {costs.by_day && costs.by_day.length > 0 && (
            <div className="card">
              <div className="section-label" style={{ marginBottom: 10 }}>Daily breakdown</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 60 }}>
                {costs.by_day.map((d: { date: string; cost: number }, i: number) => {
                  const max = Math.max(...costs.by_day.map((x: { cost: number }) => x.cost));
                  return (
                    <div key={i} title={`${d.date}: $${d.cost.toFixed(2)}`} style={{ flex: 1, borderRadius: "2px 2px 0 0", height: `${Math.max(8, (d.cost / max) * 100)}%`, background: "var(--orange)", opacity: 0.5 }} />
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-model breakdown from usage API */}
          {usage?.byModel && Object.keys(usage.byModel).length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="section-label" style={{ marginBottom: 10 }}>Cost by model</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(usage.byModel).map(([model, data]: [string, unknown]) => {
                  const d = data as { calls: number; credits: number; costUsd: number };
                  return (
                    <div key={model} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--text)" }}>{model.length > 30 ? model.slice(0, 28) + '...' : model}</span>
                        <span className="badge" style={{ fontSize: "var(--text-xs)" }}>{d.calls} calls</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span className="mono" style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{Math.round(d.credits).toLocaleString()} credits</span>
                        <span className="mono" style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text)" }}>${d.costUsd.toFixed(4)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Per-phase breakdown from usage API */}
          {usage?.byPhase && Object.keys(usage.byPhase).length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="section-label" style={{ marginBottom: 10 }}>Cost by phase</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(usage.byPhase).map(([phase, data]: [string, unknown]) => {
                  const d = data as { inputTokens: number; outputTokens: number; costUsd: number };
                  return (
                    <div key={phase} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--text)", textTransform: "capitalize" }}>{phase.replace(/_/g, ' ')}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <span className="mono" style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>{d.inputTokens >= 1_000 ? `${(d.inputTokens / 1_000).toFixed(0)}k` : d.inputTokens} in / {d.outputTokens >= 1_000 ? `${(d.outputTokens / 1_000).toFixed(0)}k` : d.outputTokens} out</span>
                        <span className="mono" style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text)" }}>${d.costUsd.toFixed(4)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {/* Per-project credit breakdown */}
          {usage?.byProject && Object.keys(usage.byProject).length > 0 && (
            <div className="card" style={{ marginTop: 16 }}>
              <div className="section-label" style={{ marginBottom: 10 }}>Usage by project</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {Object.entries(usage.byProject)
                  .sort(([, a], [, b]) => (b as any).creditsUsed - (a as any).creditsUsed)
                  .map(([project, data]: [string, unknown]) => {
                    const d = data as { runs: number; chatMessages: number; creditsUsed: number; totalCostUsd: number };
                    return (
                      <div key={project} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className="project-dot" style={{ background: "var(--cyan)" }} />
                          <span className="mono" style={{ fontSize: "var(--text-sm)", color: "var(--text)" }}>{project}</span>
                          <span className="badge" style={{ fontSize: "var(--text-xs)" }}>
                            {d.runs > 0 ? `${d.runs} runs` : ''}{d.runs > 0 && d.chatMessages > 0 ? ' · ' : ''}{d.chatMessages > 0 ? `${d.chatMessages} chats` : ''}
                          </span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span className="mono" style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)" }}>
                            {Math.round(d.creditsUsed).toLocaleString()} credits
                          </span>
                          <span className="mono" style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--text)" }}>${d.totalCostUsd.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="empty-state" style={{ border: "1px dashed var(--border)", borderRadius: "var(--radius-lg)" }}>
          <span className="muted" style={{ fontSize: "var(--text-sm)" }}>
            No cost data available for {selectedProject}. Costs are tracked as pipeline runs execute.
          </span>
        </div>
      )}
    </div>
  );
}
